/**
 * Does a stored column's data type fit what a slot accepts? (CT-5641)
 *
 * A slot states its appetite twice: `x-data-types` for the slot as a whole,
 * and `x-data-types-by-aggregate` for the narrower set each measure operation
 * takes (`SUM` wants a number, `MIN`/`MAX` also take a date, `COUNT` takes
 * anything). Both are matched against the **source column's** stored type,
 * which the DATASET carries over from the entity field.
 *
 * The two vocabularies are close but not identical — the schema says
 * `SMALLINT`, the catalog says `INTEGER`; the schema says `TIMESTAMP`, a
 * column may say `TIMESTAMPTZ` — so the mapping below is the bridge. It was
 * read out of `widgetBindingSlots` when the rev4 reader needed the same answer
 * (`widgetSlotModel`) and a second copy would have been a second set of bugs.
 */

/**
 * A field's stored data type → the type names a slot may ask for.
 *
 * A type this map has never seen matches **everything**: a column hidden from
 * the only slot it belongs in is a dead end the author cannot argue with,
 * while a wrong guess costs one publish error that says exactly what is wrong.
 */
const ACCEPTED_NAMES_BY_DATA_TYPE = Object.freeze({
  TEXT: ['TEXT'], VARCHAR: ['TEXT'], CHAR: ['TEXT'], STRING: ['TEXT'],
  URL: ['URL', 'TEXT'],
  UUID: ['UUID'],
  INTEGER: ['INTEGER'], BIGINT: ['INTEGER'], SMALLINT: ['INTEGER'],
  NUMERIC: ['NUMERIC', 'DECIMAL'], DECIMAL: ['DECIMAL', 'NUMERIC'],
  FLOAT: ['DECIMAL', 'NUMERIC'], DOUBLE: ['DECIMAL', 'NUMERIC'],
  REAL: ['DECIMAL', 'NUMERIC'], MONEY: ['MONEY', 'DECIMAL', 'NUMERIC'],
  DATE: ['DATE'],
  DATETIME: ['DATETIME', 'TIMESTAMP'],
  TIMESTAMP: ['TIMESTAMP', 'DATETIME'], TIMESTAMPTZ: ['TIMESTAMP', 'DATETIME'],
  BOOLEAN: ['BOOLEAN'], BOOL: ['BOOLEAN'],
  // No slot in either catalog accepts one, so offering a `TIME` column would
  // only produce a binding the publish validator refuses. Everything else that
  // resolves to nothing is a column the author cannot place; this one is a
  // column no type has a place for.
  TIME: [],
});

/**
 * What an aggregate **produces**, which is not always what it reads.
 *
 * The distinction is not academic: a slot states the types it accepts, and
 * the backend checks the *output* against them. `COST_KPI.amount` takes
 * `DECIMAL|NUMERIC|MONEY`, and `SUM` over an `INTEGER` column produces an
 * `INTEGER`, so it is refused at publish —
 * `UI_PRESENTATION_BINDING_TYPE_MISMATCH ({"allowed_types": ["DECIMAL",
 * "NUMERIC","MONEY"], "output_data_type": "INTEGER"})`, measured on dev
 * 2026-10-20. `AVG` over the same column is accepted, because an average is
 * not an integer.
 *
 * So offering a column for a measure slot takes two checks, and this is the
 * second one: `x-data-types-by-aggregate` says which columns the operation
 * can *read*, and this says what comes out the other side.
 *
 * @param {string} aggregate One of `WIDGET_AGGREGATES`, or `''` for a plain
 *   column, which passes its own type through.
 * @param {string} sourceDataType The column's stored type.
 */
export function aggregateOutputType(aggregate, sourceDataType) {
  if (!aggregate) return sourceDataType;
  // Counting rows, values or distinct values: always a whole number.
  if (aggregate === 'COUNT' || aggregate === 'COUNT_DISTINCT' || aggregate === 'COUNT_ROWS') {
    return 'INTEGER';
  }
  // An average has a fractional part whatever it averaged.
  if (aggregate === 'AVG') return 'NUMERIC';
  // `SUM`, `MIN` and `MAX` answer in the column's own type.
  return sourceDataType;
}

/**
 * @param {string} dataType The column's stored type.
 * @param {string[]} accepted What the slot (or the chosen aggregate) takes. An
 *   empty list is "no stated appetite", which accepts anything.
 */
export function dataTypeFits(dataType, accepted = []) {
  if (!accepted.length) return true;

  const stored = String(dataType ?? '').toUpperCase();
  const candidates = ACCEPTED_NAMES_BY_DATA_TYPE[stored];
  // Unknown to this map — see the comment on it: offer rather than hide.
  if (!candidates) return true;

  return candidates.some((candidate) => accepted.includes(candidate));
}

export default dataTypeFits;
